import { useState } from 'react';
import { toast } from 'sonner';
import { Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Badge } from '@/components/ui/Badge';
import { MoneyInput } from '@/components/money/MoneyInput';
import { EmptyState } from '@/components/ui/EmptyState';
import { MonthSwitcher } from '@/components/ui/MonthSwitcher';
import { currentYearMonth } from '@/lib/date';
import { formatCOP, formatDate } from '@/lib/format';
import { apiErrorMessage } from '@/lib/api';
import { useCategories } from '@/hooks/useCategories';
import { useCreateExpense, useDeleteExpense, useExpenses } from '@/hooks/useExpenses';
import { useDebts } from '@/hooks/useDebts';
import { useCreateRecurringExpense, useRecurringExpenses, useUpdateRecurringExpense } from '@/hooks/useRecurring';

export function ExpensesPage() {
  const { data: categories } = useCategories('EXPENSE');
  const { data: debts } = useDebts('ACTIVE');
  const [month, setMonth] = useState(currentYearMonth);
  const { data } = useExpenses({ month });
  const { data: recurringTemplates } = useRecurringExpenses();
  const createExpense = useCreateExpense();
  const createRecurringExpense = useCreateRecurringExpense();
  const updateRecurringExpense = useUpdateRecurringExpense();
  const deleteExpense = useDeleteExpense();

  const [amount, setAmount] = useState(0);
  const [description, setDescription] = useState('');
  const [categoryId, setCategoryId] = useState<number | ''>('');
  const [debtId, setDebtId] = useState<number | ''>('');
  const [spentAt, setSpentAt] = useState(() => new Date().toISOString().slice(0, 10));
  const [installmentsTotal, setInstallmentsTotal] = useState<number | ''>('');
  const [generatesInterest, setGeneratesInterest] = useState(true);
  const [isRecurring, setIsRecurring] = useState(false);

  const activeTemplates = recurringTemplates?.filter((t) => t.active) ?? [];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Gastos</h1>
          <p className="text-sm text-muted-foreground">Lo que sale de tus cuentas. Si lo pagaste con tarjeta, enlázalo a la deuda.</p>
        </div>
        <MonthSwitcher value={month} onChange={setMonth} />
      </div>

      <form
        className="card grid grid-cols-1 gap-3 sm:grid-cols-6 sm:items-end"
        onSubmit={async (e) => {
          e.preventDefault();
          if (amount <= 0 || !description) return;
          try {
            if (isRecurring && debtId === '') {
              await createRecurringExpense.mutateAsync({
                amount,
                description,
                categoryId: categoryId === '' ? null : categoryId,
                dayOfMonth: Number(spentAt.slice(8, 10)),
                startMonth: spentAt.slice(0, 7),
              });
              toast.success('Gasto recurrente creado');
            } else {
              await createExpense.mutateAsync({
                amount,
                description,
                spentAt,
                categoryId: categoryId === '' ? null : categoryId,
                debtId: debtId === '' ? null : debtId,
                paymentMethod: debtId === '' ? 'CASH' : 'CREDIT',
                installmentsTotal: debtId === '' || installmentsTotal === '' ? null : installmentsTotal,
                generatesInterest,
              });
              toast.success('Gasto registrado');
            }
            setAmount(0);
            setDescription('');
            setInstallmentsTotal('');
            setGeneratesInterest(true);
            setIsRecurring(false);
          } catch (err) {
            toast.error(apiErrorMessage(err, 'No se pudo registrar el gasto'));
          }
        }}
      >
        <div>
          <label className="text-xs font-medium text-muted-foreground">Monto</label>
          <MoneyInput value={amount} onChange={setAmount} />
        </div>
        <div>
          <label className="text-xs font-medium text-muted-foreground">Descripción</label>
          <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Mercado" />
        </div>
        <div>
          <label className="text-xs font-medium text-muted-foreground">Categoría</label>
          <Select value={categoryId} onChange={(e) => setCategoryId(e.target.value ? Number(e.target.value) : '')}>
            <option value="">Sin categoría</option>
            {categories?.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
        </div>
        <div>
          <label className="text-xs font-medium text-muted-foreground">Cargar a deuda</label>
          <Select value={debtId} onChange={(e) => setDebtId(e.target.value ? Number(e.target.value) : '')}>
            <option value="">No (efectivo/débito)</option>
            {debts?.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </Select>
        </div>
        <div>
          <label className="text-xs font-medium text-muted-foreground">
            {isRecurring && debtId === '' ? 'Primer pago (día del mes)' : 'Fecha'}
          </label>
          <Input type="date" value={spentAt} onChange={(e) => setSpentAt(e.target.value)} />
        </div>
        {debtId !== '' ? (
          <>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Cuotas (diferido, opcional)</label>
              <Input
                type="number"
                min={1}
                placeholder="Corriente"
                value={installmentsTotal}
                onChange={(e) => setInstallmentsTotal(e.target.value ? Number(e.target.value) : '')}
              />
            </div>
            <div className="flex items-center gap-2 pb-2">
              <input
                id="generatesInterest"
                type="checkbox"
                checked={generatesInterest}
                onChange={(e) => setGeneratesInterest(e.target.checked)}
                className="h-4 w-4 rounded border-input"
              />
              <label htmlFor="generatesInterest" className="text-xs font-medium text-muted-foreground">
                Genera interés
              </label>
            </div>
          </>
        ) : (
          <div className="flex items-center gap-2 sm:pb-2">
            <input
              id="isRecurringExpense"
              type="checkbox"
              className="h-4 w-4 rounded border-border"
              checked={isRecurring}
              onChange={(e) => setIsRecurring(e.target.checked)}
            />
            <label htmlFor="isRecurringExpense" className="text-xs font-medium text-muted-foreground">
              Es recurrente (ej. arriendo)
            </label>
          </div>
        )}
        <Button type="submit" disabled={createExpense.isPending || createRecurringExpense.isPending}>
          Agregar
        </Button>
      </form>

      {activeTemplates.length > 0 && (
        <div className="card space-y-2">
          <h2 className="text-sm font-medium text-muted-foreground">Recurrentes activos</h2>
          <div className="divide-y divide-border">
            {activeTemplates.map((t) => (
              <div key={t.id} className="flex items-center justify-between gap-3 py-2">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">{t.description}</p>
                  <p className="text-xs text-muted-foreground">Día {t.dayOfMonth} de cada mes</p>
                </div>
                <div className="flex shrink-0 items-center gap-3">
                  <span className="font-medium">{formatCOP(t.amount)}</span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={updateRecurringExpense.isPending}
                    onClick={async () => {
                      try {
                        await updateRecurringExpense.mutateAsync({ id: t.id, active: false });
                        toast.success('Gasto recurrente desactivado');
                      } catch (err) {
                        toast.error(apiErrorMessage(err, 'No se pudo desactivar'));
                      }
                    }}
                  >
                    Desactivar
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {data && data.items.length > 0 ? (
        <div className="card divide-y divide-border p-0">
          {data.items.map((expense) => (
            <div key={expense.id} className="flex items-center justify-between gap-3 px-4 py-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <p className="truncate text-sm font-medium">{expense.description}</p>
                  {expense.isRecurring && (
                    <Badge tone="default" className="shrink-0">
                      Recurrente
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  {formatDate(expense.spentAt)}
                  {expense.debtId ? ' · a crédito' : ''}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <span className="font-medium">{formatCOP(expense.amount)}</span>
                <button
                  type="button"
                  className="rounded-md p-2 text-muted-foreground hover:bg-accent hover:text-destructive"
                  onClick={() => deleteExpense.mutate(expense.id)}
                  aria-label="Eliminar"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <EmptyState title="No hay gastos este mes" description="Agrega tu primer gasto con el formulario de arriba." />
      )}
    </div>
  );
}
